import { Link } from "react-router-dom";
import { mainSiteUrl } from "@/mocks/categories";
import Footer from "@/components/feature/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://acbuyspreadsheet.biz";

export default function ArticlePage() {
  const guideJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "What is ACBuySpreadsheet? Full Guide & How It Works in 2026",
        "description": "Complete guide to ACBuySpreadsheet — the spreadsheet-based buying and order tracking system. Learn how acbuy spreadsheet simplifies purchasing workflow, tracks orders, and optimizes your buying experience.",
        "author": { "@type": "Organization", "name": "ACBuySpreadsheet" },
        "datePublished": "2026-06-24",
        "dateModified": "2026-06-24",
        "publisher": { "@type": "Organization", "name": "ACBuySpreadsheet", "url": siteUrl },
        "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteUrl}/acbuyspreadsheet-guide` }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Is ACBuySpreadsheet free to use?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, the core acbuy spreadsheet system is available for free. Users can access the main store, browse categories, and manage their order tracking without any subscription fees."
            }
          },
          {
            "@type": "Question",
            "name": "What product categories are available?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "ACBuySpreadsheet covers a comprehensive range of categories including Shoes, Hoodies & Sweaters, T-Shirts, Jackets, Pants & Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and more."
            }
          },
          {
            "@type": "Question",
            "name": "Can I use the spreadsheet on mobile devices?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolutely. The acbuy spreadsheet system is fully responsive and works seamlessly on smartphones, tablets, and desktop computers."
            }
          },
          {
            "@type": "Question",
            "name": "How secure is my order data?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Your data remains entirely under your control. The acbuy spreadsheet system is built with transparency in mind — you own your data and manage it directly within the spreadsheet environment."
            }
          }
        ]
      }
    ]
  };

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-background-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link
            to="/"
            className="text-lg font-heading font-bold text-foreground-900 whitespace-nowrap cursor-pointer hover:text-primary-500 transition-colors"
          >
            ACBuySpreadsheet
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 cursor-pointer transition-colors whitespace-nowrap"
            >
              Home
            </Link>
            <Link
              to="/acbuyspreadsheet-guide"
              className="px-4 py-2 text-sm font-medium text-primary-600 cursor-pointer transition-colors whitespace-nowrap"
            >
              Shopping Guide
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-full cursor-pointer transition-colors whitespace-nowrap"
            >
              buy it now
            </a>
          </div>
        </div>
      </nav>

      <article className="bg-background-50">
        <header className="bg-background-100 py-14 md:py-20">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(guideJsonLd),
              }}
            />
            <span className="inline-block px-4 py-1.5 bg-white rounded-full text-xs text-accent-700 font-medium uppercase tracking-wider mb-5">
              Guide
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight">
              What is ACBuySpreadsheet? Full Guide &amp; How It Works in 2026
            </h1>
            <div className="flex items-center gap-4 mt-5 text-xs text-foreground-400">
              <span>(Updated June 2026)</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>12 min read</span>
            </div>
          </div>
        </header>

        <div className="max-w-[800px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="prose-custom">
            <section className="mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                Introduction to ACBuySpreadsheet
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                In the fast-moving world of e-commerce and online purchasing, staying organized is everything. Whether you are managing personal shopping orders or running a small reselling operation, an <strong>acbuy spreadsheet</strong> can be the difference between chaos and clarity. <strong>ACBuySpreadsheet</strong> is a spreadsheet-based buying and order tracking system designed to bring structure, speed, and accuracy to your purchasing workflow.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Unlike traditional order management tools that rely on complex dashboards and expensive subscriptions, an <strong>acbuy spreadsheet</strong> focuses on what truly matters: clear data presentation, real-time updates, and a workflow that anyone can understand within minutes. This guide will walk you through everything you need to know about ACBuySpreadsheet — from core features to advanced usage tips.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                By the end of this guide, you will understand why thousands of users have switched to the <strong>acbuy spreadsheet</strong> system for their daily order tracking and purchasing optimization needs.
              </p>
            </section>

            <section className="mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                What Exactly is ACBuySpreadsheet?
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                <strong>ACBuySpreadsheet</strong> is a structured spreadsheet system that functions as both a buying assistant and an order tracker. At its core, it organizes product data, purchase timelines, shipping statuses, and budget calculations into a cohesive, easy-to-navigate format. Rather than juggling multiple browser tabs and email confirmations, users input their order details once into the <strong>acbuy spreadsheet</strong> and monitor everything from a single view.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                The system is built on the principle of transparency. Every row in the spreadsheet represents a tracked item, and columns capture vital information such as product names, categories, prices, order dates, estimated delivery windows, and current status. This level of detail makes the <strong>acbuy spreadsheet</strong> particularly valuable for users who place frequent orders across multiple categories — from shoes and apparel to accessories and lifestyle products.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                One of the standout features of ACBuySpreadsheet is its category-based organization system. The platform links directly to categorized product pages — including <strong>Shoes</strong>, <strong>Hoodies &amp; Sweaters</strong>, <strong>T-Shirts</strong>, <strong>Jackets</strong>, <strong>Pants &amp; Shorts</strong>, <strong>Headwear</strong>, <strong>Sets</strong>, <strong>Underwear</strong>, <strong>Jersey</strong>, <strong>Accessories</strong>, <strong>Bags</strong>, <strong>Belts</strong>, <strong>Perfumes</strong>, and more — giving users a seamless path from browsing to tracking.
              </p>
            </section>

            <section className="mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                How the ACBuySpreadsheet System Works
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Using the <strong>acbuy spreadsheet</strong> is refreshingly straightforward. The workflow can be broken down into three clear steps:
              </p>

              <div className="space-y-5 my-6">
                <div className="flex gap-4 p-5 bg-white rounded-2xl">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm">1</span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">Access the System</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      Navigate to the main store through ACBuySpreadsheet. Choose your preferred product category — whether it is Shoes, Jackets, T-Shirts, or Accessories — and begin browsing the organized catalog.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 p-5 bg-white rounded-2xl">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-bold text-sm">2</span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">Input or Track Order Data</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      Enter your order details into the spreadsheet tracker. Record product names, quantities, prices, and expected delivery dates. The <strong>acbuy spreadsheet</strong> keeps everything neatly organized so you never lose track of a single purchase.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 p-5 bg-white rounded-2xl">
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600 font-bold text-sm">3</span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">Monitor Updates in Real-Time</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      As orders progress through shipping and delivery, update their statuses in the spreadsheet. The real-time dashboard view gives you an instant overview of all pending, shipped, and delivered items.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                Key Benefits of Using an ACBuy Spreadsheet
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                <div className="bg-white rounded-2xl p-5">
                  <span className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-flashlight-line text-accent-600 text-sm"></i>
                    </span>
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">Fast &amp; Structured Workflow</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    The <strong>acbuy spreadsheet</strong> eliminates guesswork. Every order follows a consistent data format, making it easy to scan, sort, and filter your entire purchase history in seconds.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-5">
                  <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-inbox-line text-primary-500 text-sm"></i>
                    </span>
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">Simplified Order Tracking</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    No more digging through email inboxes. The ACBuySpreadsheet system consolidates all order information — product names, tracking numbers, delivery statuses — into one accessible location.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-5">
                  <span className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-secondary-500 text-sm"></i>
                    </span>
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">Transparent Data Management</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    Every data point in the <strong>acbuy spreadsheet</strong> is visible and editable. There are no hidden algorithms or black-box reports — just clean, honest data that you control completely.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-5">
                  <span className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-line-chart-line text-accent-600 text-sm"></i>
                    </span>
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">Built for Scalability</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    Whether you track 10 orders a month or 1,000, the spreadsheet system scales effortlessly. The structure remains consistent regardless of volume, making the <strong>acbuy spreadsheet</strong> ideal for growing operations.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                Who Should Use ACBuySpreadsheet?
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                The <strong>acbuy spreadsheet</strong> is designed for anyone who values organization in their purchasing process. This includes:
              </p>
              <ul className="space-y-3 my-5">
                <li className="flex gap-3 text-sm text-foreground-700">
                  <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-checkbox-circle-line text-accent-500"></i>
                  </span>
                  <span><strong>Frequent online shoppers</strong> who place orders across multiple stores and need a centralized tracking system.</span>
                </li>
                <li className="flex gap-3 text-sm text-foreground-700">
                  <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-checkbox-circle-line text-accent-500"></i>
                  </span>
                  <span><strong>Small business owners and resellers</strong> managing inventory purchases from various suppliers.</span>
                </li>
                <li className="flex gap-3 text-sm text-foreground-700">
                  <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-checkbox-circle-line text-accent-500"></i>
                  </span>
                  <span><strong>Procurement teams</strong> looking for a lightweight alternative to enterprise resource planning software.</span>
                </li>
                <li className="flex gap-3 text-sm text-foreground-700">
                  <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-checkbox-circle-line text-accent-500"></i>
                  </span>
                  <span><strong>Budget-conscious individuals</strong> who want to monitor spending patterns and identify saving opportunities.</span>
                </li>
              </ul>
            </section>

            <section className="mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                ACBuySpreadsheet vs Traditional Order Management
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Many users wonder how the <strong>acbuy spreadsheet</strong> compares to conventional order management tools. The table below highlights the key differences:
              </p>

              <div className="overflow-x-auto my-6 rounded-2xl border border-background-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left px-5 py-3 font-semibold text-foreground-800">Feature</th>
                      <th className="text-left px-5 py-3 font-semibold text-foreground-800">ACBuySpreadsheet</th>
                      <th className="text-left px-5 py-3 font-semibold text-foreground-800">Traditional Tools</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-100">
                    <tr className="bg-white">
                      <td className="px-5 py-3 text-foreground-700">Setup Time</td>
                      <td className="px-5 py-3 text-accent-700 font-medium">Minutes</td>
                      <td className="px-5 py-3 text-foreground-500">Hours to Days</td>
                    </tr>
                    <tr>
                      <td className="px-5 py-3 text-foreground-700">Learning Curve</td>
                      <td className="px-5 py-3 text-accent-700 font-medium">Minimal</td>
                      <td className="px-5 py-3 text-foreground-500">Moderate to Steep</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-5 py-3 text-foreground-700">Cost</td>
                      <td className="px-5 py-3 text-accent-700 font-medium">Low / Free</td>
                      <td className="px-5 py-3 text-foreground-500">Monthly Subscription</td>
                    </tr>
                    <tr>
                      <td className="px-5 py-3 text-foreground-700">Customization</td>
                      <td className="px-5 py-3 text-accent-700 font-medium">Fully Flexible</td>
                      <td className="px-5 py-3 text-foreground-500">Limited by Platform</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-5 py-3 text-foreground-700">Data Ownership</td>
                      <td className="px-5 py-3 text-accent-700 font-medium">100% Yours</td>
                      <td className="px-5 py-3 text-foreground-500">Vendor Dependent</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                Getting Started with ACBuySpreadsheet
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Ready to transform your purchasing workflow? Getting started with the <strong>acbuy spreadsheet</strong> system is simple. Browse our complete product categories — from <strong>Shoes</strong> and <strong>T-Shirts</strong> to <strong>Jackets</strong>, <strong>Bags</strong>, and <strong>Accessories</strong> — and begin tracking your orders with precision and confidence. The system is designed to grow with you, adapting to your needs whether you are a casual shopper or a high-volume buyer.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                The <strong>acbuy spreadsheet</strong> approach emphasizes simplicity and clarity. There are no unnecessary features, no bloated interfaces, and no steep learning curves. Just a clean, powerful spreadsheet system that helps you stay on top of every purchase.
              </p>
            </section>

            <section className="mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                <div className="bg-white rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                    Is ACBuySpreadsheet free to use?
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    Yes, the core <strong>acbuy spreadsheet</strong> system is available for free. Users can access the main store, browse categories, and manage their order tracking without any subscription fees.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                    What product categories are available?
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    ACBuySpreadsheet covers a comprehensive range of categories including Shoes, Hoodies &amp; Sweaters, T-Shirts, Jackets, Pants &amp; Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and more. Each category links to a curated product selection.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                    Can I use the spreadsheet on mobile devices?
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    Absolutely. The <strong>acbuy spreadsheet</strong> system is fully responsive and works seamlessly on smartphones, tablets, and desktop computers, allowing you to track orders wherever you are.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                    How secure is my order data?
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    Your data remains entirely under your control. The <strong>acbuy spreadsheet</strong> system is built with transparency in mind — you own your data and manage it directly within the spreadsheet environment.
                  </p>
                </div>
              </div>
            </section>

            <div className="mt-12 md:mt-16 p-6 md:p-8 bg-accent-50 rounded-3xl text-center">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">
                Start Using ACBuySpreadsheet Today
              </h2>
              <p className="text-sm text-foreground-600 mb-6 max-w-[500px] mx-auto leading-relaxed">
                Join thousands of users who have optimized their purchasing workflow with the most efficient <strong>acbuy spreadsheet</strong> system available.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-full cursor-pointer transition-colors whitespace-nowrap"
                >
                  Enter ACBuySpreadsheet System
                </a>
                <Link
                  to="/"
                  className="px-6 py-3 bg-white hover:bg-background-100 text-foreground-800 text-sm font-medium rounded-full cursor-pointer transition-colors whitespace-nowrap border border-background-200"
                >
                  &larr; Back to Homepage
                </Link>
              </div>
            </div>

            <nav className="mt-10 pt-8 border-t border-background-200 flex flex-wrap items-center gap-4 text-xs text-foreground-400">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                &larr; ACBuySpreadsheet Homepage
              </Link>
              <span className="text-foreground-200">|</span>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="hover:text-primary-500 transition-colors cursor-pointer">
                Main Store
              </a>
              <span className="text-foreground-200">|</span>
              <span>ACBuySpreadsheet Guide (2026)</span>
            </nav>
          </div>
        </div>
      </article>

      <Footer />
    </>
  );
}